// Imperative marker + label reconciliation, kept OUT of Svelte reactivity.
//
// A manager owns a registry keyed by entity id and diffs it against a plain
// data array: create new, update changed in place (equality-gated), destroy
// orphans. A `draggingId` guard + position equality-gate prevent the store
// echo from fighting an in-flight drag (no flicker).

import { getLabelOverlayClass, type LabelInstance, type LabelKind } from './map';
import { householdSize } from '../household';
import { isMultiTouch, onMultiTouchChange } from './touch';

/**
 * A plain ringed-circle icon. The emoji is layered on top via the marker's
 * `label` (see emojiGlyph) rather than drawn into the SVG — the browser centers
 * label text reliably, where SVG `<text>` baselines render color emoji off.
 */
export function ringIcon(size: number, ringColor: string): google.maps.Icon {
  const c = size / 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">` +
    `<circle cx="${c}" cy="${c}" r="${c - 1.5}" fill="white" stroke="${ringColor}" stroke-width="2"/></svg>`;
  return {
    url: 'data:image/svg+xml;utf8,' + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(c, c),
    labelOrigin: new google.maps.Point(c, c),
  };
}

/** A marker label that renders a centered emoji glyph. */
export const emojiGlyph = (emoji: string, fontSize: number): google.maps.MarkerLabel => ({
  text: emoji,
  fontSize: `${fontSize}px`,
  className: 'marker-glyph',
});

export const friendGlyph = (count?: number) => emojiGlyph('🙂'.repeat(Math.min(3, householdSize(count))), 20);
export const TRIP_GLYPH = emojiGlyph('📍', 20);
export const candidateGlyph = (): google.maps.MarkerLabel => emojiGlyph('🏠', 20);

// Border color for a marker that's inside the active travel radius. Baked into
// the icon itself (not a separate overlay) so it stays welded to the marker —
// it drags with it and can't overlap other map chrome.
const REACH_COLOR = '#c8b6ff';

function bareIcon(width: number, accent?: string): google.maps.Icon {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="30">${accent ? `<circle cx="${width / 2}" cy="27" r="2.5" fill="${accent}"/>` : ''}</svg>`;
  return {
    url: 'data:image/svg+xml;utf8,' + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(width, 30),
    anchor: new google.maps.Point(width / 2, 15),
    labelOrigin: new google.maps.Point(width / 2, 15),
  };
};
export const friendIcon = (reachable = false, count?: number): google.maps.Icon =>
  bareIcon(Math.min(3, householdSize(count)) * 22, reachable ? REACH_COLOR : undefined);
export const candidateIcon = (color?: string): google.maps.Icon => bareIcon(22, color);
export const tripIcon = (reachable = false): google.maps.Icon => bareIcon(22, reachable ? REACH_COLOR : undefined);

export interface Entity {
  id: string;
  lat: number;
  lng: number;
  name: string;
}

export interface ManagerOpts<T extends Entity> {
  kind: LabelKind;
  zIndex: number;
  draggable?: boolean;
  icon: (e: T) => google.maps.Icon;
  /** Emoji glyph rendered centered on the marker (via the native marker label). */
  glyph?: (e: T) => google.maps.MarkerLabel | undefined;
  householdSize?: (e: T) => number;
  labelText?: (e: T) => string;
  labelColor?: (e: T) => string | null;
  title?: (e: T) => string;
  onClick?: (id: string) => void;
  onDragEnd?: (id: string, latLng: google.maps.LatLng) => void;
}

interface Entry {
  marker: google.maps.Marker;
  label: LabelInstance;
  lat: number;
  lng: number;
}

const EPS = 1e-9;

export interface MarkerManager<T extends Entity> {
  sync(map: google.maps.Map, list: T[]): void;
  destroy(): void;
}

export function createMarkerManager<T extends Entity>(opts: ManagerOpts<T>): MarkerManager<T> {
  const reg = new Map<string, Entry>();
  let draggingId: string | null = null;
  const baseDraggable = opts.draggable ?? true;

  // When a second finger lands on the map, the user is pinch-zooming / panning
  // the MAP — pins must not steal that gesture. Disable dragging on every marker
  // for the duration, then restore. (No-op on desktop, which never fires touch.)
  const offMultiTouch = onMultiTouchChange((multi) => {
    if (!baseDraggable) return;
    for (const [, entry] of reg) entry.marker.setDraggable(!multi);
  });

  function create(map: google.maps.Map, e: T): Entry {
    const marker = new google.maps.Marker({
      position: { lat: e.lat, lng: e.lng },
      map,
      title: opts.title ? opts.title(e) : e.name,
      zIndex: opts.zIndex,
      // A marker created mid-pinch must not be draggable either.
      draggable: baseDraggable && !isMultiTouch(),
      icon: opts.icon(e),
      label: opts.glyph ? opts.glyph(e) : undefined,
    });
    const Label = getLabelOverlayClass();
    const label = new Label({ lat: e.lat, lng: e.lng }, opts.labelText ? opts.labelText(e) : e.name, opts.kind, opts.labelColor ? opts.labelColor(e) : null);
    if (opts.householdSize) label.setHouseholdSize(opts.householdSize(e));
    label.setMap(map);

    const id = e.id;
    // Did a second finger appear at any point during this drag? If so the drag
    // was an accidental byproduct of a two-finger gesture and must be discarded.
    let dragMulti = false;
    if (opts.onClick) marker.addListener('click', () => opts.onClick!(id));
    marker.addListener('dragstart', () => {
      dragMulti = isMultiTouch();
    });
    marker.addListener('drag', (ev: google.maps.MapMouseEvent) => {
      draggingId = id;
      if (isMultiTouch()) { dragMulti = true; return; }
      if (ev.latLng) label.setPosition({ lat: ev.latLng.lat(), lng: ev.latLng.lng() });
    });
    marker.addListener('dragend', (ev: google.maps.MapMouseEvent) => {
      draggingId = null;
      if (dragMulti || isMultiTouch()) {
        // Snap back to the committed position and skip the move — the user was
        // manipulating the map, not relocating this pin.
        dragMulti = false;
        const committed = reg.get(id);
        const lat = committed ? committed.lat : e.lat;
        const lng = committed ? committed.lng : e.lng;
        marker.setPosition({ lat, lng });
        label.setPosition({ lat, lng });
        return;
      }
      if (ev.latLng && opts.onDragEnd) opts.onDragEnd(id, ev.latLng);
    });
    return { marker, label, lat: e.lat, lng: e.lng };
  }

  function sync(map: google.maps.Map, list: T[]): void {
    const seen = new Set<string>();
    for (const e of list) {
      seen.add(e.id);
      let entry = reg.get(e.id);
      if (!entry) {
        reg.set(e.id, create(map, e));
        continue;
      }
      // Mid-drag: record the data but don't fight the live marker position.
      if (draggingId === e.id) {
        entry.lat = e.lat;
        entry.lng = e.lng;
        continue;
      }
      if (Math.abs(entry.lat - e.lat) > EPS || Math.abs(entry.lng - e.lng) > EPS) {
        entry.marker.setPosition({ lat: e.lat, lng: e.lng });
        entry.label.setPosition({ lat: e.lat, lng: e.lng });
        entry.lat = e.lat;
        entry.lng = e.lng;
      }
      entry.label.setText(opts.labelText ? opts.labelText(e) : e.name);
      entry.marker.setIcon(opts.icon(e));
      if (opts.glyph) entry.marker.setLabel(opts.glyph(e) ?? null);
      if (opts.householdSize) entry.label.setHouseholdSize(opts.householdSize(e));
    }
    for (const [id, entry] of reg) {
      if (!seen.has(id)) {
        entry.marker.setMap(null);
        entry.label.setMap(null);
        reg.delete(id);
      }
    }
  }

  function destroy(): void {
    offMultiTouch();
    for (const [, entry] of reg) {
      entry.marker.setMap(null);
      entry.label.setMap(null);
    }
    reg.clear();
  }

  return { sync, destroy };
}
